import { t } from 'i18next';
import Cookies from 'js-cookie';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Actions, StorageState } from '../actions/enums';
import { createList } from '../network/endpoints/favourites/createList';
import { deleteItemFromList } from '../network/endpoints/favourites/deleteItemFromList';
import { getItemMetadataCompact } from '../network/endpoints/favourites/getItemMetadataCompact';
import { getLists } from '../network/endpoints/favourites/getLists';
import { postItem } from '../network/endpoints/favourites/postItem';
import { postUserReplacement } from '../network/endpoints/favourites/postUserReplacement';
import { Cookie, Storage } from '../network/enums';
import { ErrorType, List } from '../network/types';
import { getCookieDomain } from '../network/utils/getCookieDomain';
import { getRetailUnitAndLanguage } from '../network/utils/getRetailUnitAndLanguage';
import { getRetailUnitCookiePath } from '../network/utils/getRetailUnitCookiePath';
import { goToFavouritesList } from '../network/utils/goToFavouritesList';
import { IdentityContext, SessionState } from '../providers/IdentityProvider';
import { ItemsImages, getItemImagesFromLists } from '../utils/getItemImagesFromLists';
import { useFavouritesAnalytics } from './useFavouritesAnalytics';
import { RedirectContext } from '../providers/RedirectProvider';
import appMetaData from '@mvecom/common/utils/appMetadata';
import { Application } from '@mvecom/common/enums/Application';

type ListImage = {
  altText: string;
  src: string;
  srcSet?: string;
};

type ListAddItem = {
  id: string;
  name: string;
  image?: ListImage;
};

type ListDeleteItem = {
  id: string;
  name: string;
  image?: ListImage;
  itemIds: string[];
};

type RemovedLists = {
  name: string;
};

export type TRemoveFromFavouritesPayload = {
  productName: string;
  productNumber: string;
  productType?: string;
  listId?: string;
  source: string;
};

export type TRemoveFromFavouritesPayloadResponse = {
  productName: string;
  productNumber: string;
  productType: string;
  listIdsRemoveItem?: string[];
  addedItemId?: string;
  listId?: string;
  listName?: string;
  skipToast?: boolean;
  keepIcon?: boolean;
  source: string;
  quantity: string | number;
};

export type TRemoveFromFavouritesPayloadResponseError = {
  productName: string;
  productNumber: string;
  productType?: string;
  skipToast?: boolean;
  listId?: string;
  listName?: string;
};

export type TAddToFavouritesPayload = {
  productNumber: string;
  productType: string;
  quantity: string | number;
  productName: string;
  listId?: string;
  source: string;
};

export type TAddToFavouritesPayloadResponse = {
  productNumber: string;
  productType: string;
  quantity: number;
  productName: string;
  listId: string;
  listName?: string;
  itemId: string;
  source: string;
};

export type TAddToFavouritesPayloadResponseError = {
  productNumber: string;
  productType: string;
  quantity: number;
  productName: string;
  listId?: string;
  listName?: string;
  skipToast?: boolean;
  source: string;
};

type Error = {
  message?: string;
  type: string;
  status?: string | number;
};

interface DefaultList {
  id: string;
  name: string;
}

let lastItemCookieValue = '';

export const useFavourites = () => {
  const { sessionState, getSession } = useContext(IdentityContext);

  const { signUpRedirect, loginRedirect } = useContext(RedirectContext);
  const { sendAddToWishlistEvent, sendRemoveFromWishlistEvent } = useFavouritesAnalytics();

  const [itemToAdd, setItemToAdd] = useState<TAddToFavouritesPayload | undefined>();
  const [listsToAddItem, setListsToAddItem] = useState<ListAddItem[]>([]);
  const [showAddItemToListModal, setShowAddItemToListModal] = useState(false);

  const [itemToRemove, setItemToRemove] = useState<TRemoveFromFavouritesPayload | undefined>();
  const [listsToRemoveItem, setListsToRemoveItem] = useState<ListDeleteItem[]>([]);
  const [removedLists, setRemovedLists] = useState<RemovedLists[]>([]);
  const [showAddOrRemoveModal, setShowAddOrRemoveModal] = useState(false);

  const [fetchItemsState, setFetchItemsState] = useState(false);
  const [showRemovedListsModal, setShowRemovedListsModal] = useState(false);
  const [favouritesAgentLoaded, setFavouritesAgentLoaded] = useState(false);

  const blockTryToRemoveFromFavourites = useRef(false);

  const favouritesItemState = useMemo(
    () => `${Storage.FAVOURITES_ITEM_STATE}_${getRetailUnitAndLanguage().retailUnit}`,
    [],
  );

  const onFavouritesPage = appMetaData.getAppName() === Application.FAVOURITES;

  const getLists = useGetLists();

  /**
   * Fetch all items from list to update item state in cookie if cookie doesn't exist.
   */
  useEffect(() => {
    if (sessionState === SessionState.DONE) {
      (async () => {
        if (globalThis.localStorage?.getItem(favouritesItemState) === undefined && !fetchItemsState) {
          setFetchItemsState(true);
          const lists = await getLists(true);
          const allItemsNumbers = [
            ...Array.from(new Set(lists.flatMap((list) => (list.items ? list.items.map((item) => item.number) : [])))),
          ];
          globalThis.localStorage?.setItem(favouritesItemState, encodeItemStorageValue(allItemsNumbers));
        }
      })();
    }
  }, [favouritesItemState, fetchItemsState, sessionState, getLists]);

  /**
   * Publish FAVOURITES_UPDATED event when items state changes
   */
  useEffect(() => {
    if (lastItemCookieValue !== globalThis.localStorage?.getItem(favouritesItemState)) {
      globalThis.ikea.pubsub.publish(Actions.FAVOURITES_UPDATED, {
        favouritedItems: decodeItemStorageValue(globalThis.localStorage?.getItem(favouritesItemState)),
      });
      lastItemCookieValue = globalThis.localStorage?.getItem(favouritesItemState) ?? '';
    }
  });

  const decodeItemStorageValue = (state: string | null) =>
    decodeURIComponent(state ?? '')
      .split(',')
      .filter((item) => !!item);

  const encodeItemStorageValue = (items: string[]) => encodeURIComponent(items.join(','));

  const addItemToState = useCallback(
    (itemNo: string): void => {
      const items = decodeItemStorageValue(globalThis.localStorage?.getItem(favouritesItemState));
      if (items.includes(itemNo)) return;
      items.push(itemNo);
      globalThis.localStorage?.setItem(favouritesItemState, encodeItemStorageValue(items));
    },
    [favouritesItemState],
  );

  const removeItemFromState = useCallback(
    (itemNo: string): void => {
      const items = decodeItemStorageValue(globalThis.localStorage?.getItem(favouritesItemState));
      const index = items.findIndex((value) => value === itemNo);
      if (index === -1) return;
      items.splice(index, 1);
      globalThis.localStorage?.setItem(favouritesItemState, encodeItemStorageValue(items));
    },
    [favouritesItemState],
  );

  const resetAddItem = () => {
    setItemToAdd(undefined);
    setListsToAddItem([]);
  };
  const resetRemoveItem = () => {
    setItemToRemove(undefined);
    setListsToRemoveItem([]);
  };

  /* Success and Error Responses */
  const addToFavouritesSuccess = useCallback(
    async (e: TAddToFavouritesPayloadResponse, skipLink = false) => {
      globalThis.ikea.pubsub.publish(Actions.ADD_TO_FAVOURITES_SUCCESS, e);
      sendAddToWishlistEvent(e, e.listId);

      const lists = await getLists(true);
      const selectedList = lists.find((x) => x.id === e.listId);

      let text;

      if (e.source === 'cart') {
        text =
          lists.length > 1
            ? t('fa.mvAgent.toast.moveToFav.selectedList.label', {
                item: e.productName,
                list: selectedList?.name,
              })
            : t('fa.mvAgent.toast.moveToFav.label', { item: e.productName });
      } else {
        text = t('fa.toast.addToFavourites.label', { item: e.productName });
      }

      globalThis.ikea?.toast(
        text,
        !skipLink
          ? {
              label: t('fa.toast.button.view.label'),
              callbackOrHref: () => goToFavouritesList(e.listId),
            }
          : undefined,
      );

      addItemToState(e.productNumber);
      resetAddItem();
      resetRemoveItem();
    },
    [addItemToState, getLists, sendAddToWishlistEvent],
  );

  const addToFavouritesFail = useCallback((error: any, e: TAddToFavouritesPayloadResponseError) => {
    globalThis.ikea.pubsub.publish(Actions.ADD_TO_FAVOURITES_FAIL, {
      errorMessage: error.message,
      ...e,
    });

    let text;

    if (!e.skipToast) {
      if (error.type === ErrorType.INVALID_QUANTITY) {
        text = t('fa.toast.maxQuantityError.text');
      } else if (e.source === 'cart') {
        text = t('fa.mvAgent.toast.moveToFavError.unsuccessful.label', { item: e.productName });
      } else {
        text = t('fa.toast.addToFavourites.errorLabel', { item: e.productName });
      }

      globalThis.ikea?.toast(text);
    }

    resetAddItem();
    resetRemoveItem();
  }, []);

  const removeFromFavouritesSuccess = useCallback(
    (e: TRemoveFromFavouritesPayloadResponse) => {
      globalThis.ikea.pubsub.publish(Actions.REMOVE_FROM_FAVOURITES_SUCCESS, e);

      e.listIdsRemoveItem?.forEach((listId) => {
        sendRemoveFromWishlistEvent(e, listId);
      });

      if (!e.keepIcon) {
        removeItemFromState(e.productNumber);
      }

      resetAddItem();
      resetRemoveItem();

      globalThis.ikea?.toast(
        e.listName
          ? t('fa.toast.removeItemFromList.label', {
              item: e.productName,
              list: e.listName,
            })
          : t('fa.toast.removeItemFromLists.label', {
              item: e.productName,
            }),
      );
    },
    [removeItemFromState, sendRemoveFromWishlistEvent],
  );

  const removeFromFavouritesFail = useCallback((error: any, e: TRemoveFromFavouritesPayloadResponseError) => {
    globalThis.ikea.pubsub.publish(Actions.REMOVE_FROM_FAVOURITES_FAIL, {
      errorMessage: error.message,
      ...e,
    });
    resetAddItem();
    resetRemoveItem();
  }, []);

  /* Sub functions */
  const addToFavourites = useCallback(
    async (listId: string, e: TAddToFavouritesPayload, listName?: string, skipLink = false) => {
      const response = await postItem(listId, {
        itemNumber: e.productNumber,
        itemType: e.productType,
        quantity: parseInt(e.quantity as string, 10),
      });
      const payload: TAddToFavouritesPayloadResponse = {
        ...e,
        quantity: parseInt(e.quantity as string, 10),
        listId,
        itemId: response.results.item.id,
      };
      if (listName) payload.listName = listName;
      await addToFavouritesSuccess(payload, skipLink);
    },
    [addToFavouritesSuccess],
  );

  const handleAddItem = (itemImage: ItemsImages | undefined, defaultList: DefaultList) => {
    setListsToAddItem((prevState) => [
      ...prevState,
      !itemImage ? defaultList : { ...defaultList, image: itemImage.image },
    ]);
  };

  const handleRemoveItem = (itemImage: ItemsImages | undefined, itemIds: string[], defaultList: DefaultList) => {
    const defaultListToRemove = { ...defaultList, itemIds: itemIds };
    setListsToRemoveItem((prevState) => [
      ...prevState,
      !itemImage ? defaultListToRemove : { ...defaultListToRemove, image: itemImage.image },
    ]);
  };

  const addOrRemoveItem = useCallback(async (e: TRemoveFromFavouritesPayload, listsResults: List[]) => {
    setItemToRemove(e);
    setItemToAdd({
      ...e,
      quantity: 1,
    } as TAddToFavouritesPayload);
    const itemsImages = await getItemImagesFromLists(listsResults);
    listsResults.forEach((list) => {
      const itemIds: string[] = [];
      if (list.items) {
        list.items?.forEach((item) => {
          if (item.number === e.productNumber) {
            itemIds.push(item.id);
          }
        });
      }
      const defaultList: DefaultList = { id: list.id, name: list.name };
      const itemImage = itemsImages.find((item) => list.items?.length && item.itemNumber === list.items[0].number);
      if (itemIds.length > 0) {
        handleRemoveItem(itemImage, itemIds, defaultList);
      } else {
        handleAddItem(itemImage, defaultList);
      }
    });
    setShowAddOrRemoveModal(true);
  }, []);

  const removeFromList = useCallback(
    async (e: TRemoveFromFavouritesPayload, list: List) => {
      const items = list?.items ?? [];
      await Promise.all(
        items.map((item) => {
          if (e.productNumber === item.number && e.productType === item.type)
            return deleteItemFromList(list.id, item.id);
        }),
      );
      removeFromFavouritesSuccess({
        ...e,
        listIdsRemoveItem: [list.id],
        listName: list.name,
      } as TRemoveFromFavouritesPayloadResponse);
    },
    [removeFromFavouritesSuccess],
  );

  const getProductType = useCallback(async (e: TRemoveFromFavouritesPayload) => {
    const productType = e.productType ?? (await getItemMetadataCompact(e.productNumber)).results[0].type;
    if (!productType) {
      throw new Error('Product type not found');
    }
    return productType;
  }, []);

  /* Main functions */
  const tryToAddToFavourites = useCallback(
    async (e: TAddToFavouritesPayload) => {
      globalThis.ikea.pubsub.publish(Actions.ADD_TO_FAVOURITES_INITIATE, e);
      try {
        if (e.listId) {
          await addToFavourites(e.listId, e, undefined, true);
          return;
        }
        const simpleListsResponse = await getLists(true);
        if (simpleListsResponse.length === 0) {
          const { id, name } = (await createList(t('fa.mvAgent.createList.defaultNewListName.text') ?? undefined))
            .results;
          await addToFavourites(id, e, name);
          return;
        }
        if (simpleListsResponse.length === 1) {
          const [list] = simpleListsResponse;
          const { id } = list;
          await addToFavourites(id, e);
          return;
        }
        setItemToAdd(e);
        const itemsImages = await getItemImagesFromLists(simpleListsResponse);
        setListsToAddItem(
          simpleListsResponse.map((list) => {
            const defaultList = { id: list.id, name: list.name };
            const itemImage = itemsImages.find((item) => list.items && item.itemNumber === list.items[0]?.number);
            if (!itemImage) return defaultList;
            return { id: list.id, name: list.name, image: itemImage.image };
          }),
        );
        setShowAddItemToListModal(true);
      } catch (error) {
        addToFavouritesFail(error as Error, { ...e, quantity: parseInt(e.quantity as string, 10) });
      }
    },
    [addToFavourites, addToFavouritesFail, getLists],
  );

  const tryToRemoveFromFavourites = useCallback(
    async (e: TRemoveFromFavouritesPayload) => {
      if (blockTryToRemoveFromFavourites.current) {
        return;
      }
      blockTryToRemoveFromFavourites.current = true;
      globalThis.ikea.pubsub.publish(Actions.REMOVE_FROM_FAVOURITES_INITIATE, e);
      try {
        const [lists, productPayload] = await Promise.all([
          getLists(true),
          getProductType(e).then((productType) => ({ ...e, productType })),
        ]);
        if (lists.length < 1) {
          throw new Error(
            `There are no lists with given item: ${productPayload.productType}-${productPayload.productName}`,
          );
        }
        if (lists.length === 1) {
          await removeFromList(productPayload, lists[0]);
          blockTryToRemoveFromFavourites.current = false;
          return;
        }
        await addOrRemoveItem(productPayload, lists);
      } catch (error) {
        removeFromFavouritesFail(error as Error, e);
      }
      blockTryToRemoveFromFavourites.current = false;
    },
    [addOrRemoveItem, getProductType, removeFromFavouritesFail, removeFromList, getLists],
  );

  /* Listener functions */
  const addToFavouritesListenerCallback = useCallback(
    async (e: TAddToFavouritesPayload) => {
      await getSession();
      await tryToAddToFavourites(e);
    },
    [getSession, tryToAddToFavourites],
  );

  const removeFromFavouritesListenerCallback = useCallback(
    async (e: TRemoveFromFavouritesPayload) => {
      await getSession();
      await tryToRemoveFromFavourites(e);
    },
    [getSession, tryToRemoveFromFavourites],
  );

  const listenForAddToFavourites = useCallback(() => {
    globalThis.ikea.pubsub.subscribe(Actions.ADD_TO_FAVOURITES_ACTION, addToFavouritesListenerCallback);
  }, [addToFavouritesListenerCallback]);

  const listenForRemoveFromFavourites = useCallback(() => {
    globalThis.ikea.pubsub.subscribe(Actions.REMOVE_FROM_FAVOURITES_ACTION, removeFromFavouritesListenerCallback);
  }, [removeFromFavouritesListenerCallback]);

  const userLoginOrSignUpCallback = useCallback(
    async (signUp?: boolean) => {
      try {
        await getSession(true);
        const userId = Cookies.get(Cookie.FAVOURITES_GUEST_ID);
        const userProvider = Cookies.get(Cookie.FAVOURITES_GUEST_PROVIDER);
        if (userId && userProvider) {
          const response = await postUserReplacement(userId, userProvider);
          const removedLists = response.results.deletedLists;
          const hasRemovedLists = removedLists?.length > 0;
          Cookies.remove(Cookie.FAVOURITES_GUEST_ID, { domain: getCookieDomain, path: getRetailUnitCookiePath });
          Cookies.remove(Cookie.FAVOURITES_GUEST_PROVIDER, { domain: getCookieDomain, path: getRetailUnitCookiePath });
          const oldItemState = globalThis.localStorage.getItem(favouritesItemState);
          globalThis.localStorage?.removeItem(favouritesItemState);
          lastItemCookieValue = '';
          setFetchItemsState(false);
          setRemovedLists(removedLists);
          setShowRemovedListsModal(hasRemovedLists);
          const toastWelcomeMessage = signUp ? 'fa.mvAgent.toast.accountCreated.label' : 'fa.mvAgent.toast.login.label';
          if (onFavouritesPage) {
            globalThis.ikea?.toast(t(oldItemState ? 'fa.mvAgent.toast.loginListsAdded.label' : toastWelcomeMessage));
          }

          if (hasRemovedLists) {
            sessionStorage.setItem(StorageState.SHOW_FAVOURITES_MERGE_EXCEEDED_MAX_AMOUNT_PROMPT, 'true');
          }
        }
        globalThis.ikea.pubsub.publish(Actions.USER_LOGGED_IN_AGENT_POST_FAVOURITES_WORK_SUCCESS, null);
      } catch {
        globalThis.ikea.pubsub.publish(Actions.USER_LOGGED_IN_AGENT_POST_FAVOURITES_WORK_FAIL, null);
      }
    },
    [favouritesItemState, getSession, onFavouritesPage],
  );

  const userSignUpCallback = useCallback(() => userLoginOrSignUpCallback(true), [userLoginOrSignUpCallback]);
  const userLoginCallback = useCallback(() => userLoginOrSignUpCallback(false), [userLoginOrSignUpCallback]);

  const listenForUserLogin = useCallback(() => {
    globalThis.ikea.pubsub.subscribe(Actions.USER_LOGGED_IN, userLoginCallback);
  }, [userLoginCallback]);
  const listenForUserSignUp = useCallback(() => {
    globalThis.ikea.pubsub.subscribe(Actions.PROFILE_SIGN_UP, userSignUpCallback);
  }, [userSignUpCallback]);

  const userLogoutCallback = useCallback(async () => {
    try {
      Cookies.set(Cookie.USER_LOGGEDIN, 'false', { domain: getCookieDomain, path: getRetailUnitCookiePath });
      globalThis.localStorage?.removeItem(favouritesItemState);
      lastItemCookieValue = '';
      globalThis.ikea.pubsub.publish(Actions.USER_LOGGED_OUT_AGENT_POST_FAVOURITES_WORK_SUCCESS, null);
    } catch {
      globalThis.ikea.pubsub.publish(Actions.USER_LOGGED_OUT_AGENT_POST_FAVOURITES_WORK_FAIL, null);
    }
  }, [favouritesItemState]);

  const listenForUserLogout = useCallback(() => {
    globalThis.ikea.pubsub.subscribe(Actions.USER_LOGGED_OUT, userLogoutCallback);
  }, [userLogoutCallback]);

  const execSubscribeListeners = useCallback(() => {
    if (!globalThis?.ikea && !globalThis.ikea?.pubsub) {
      return;
    }
    listenForAddToFavourites();
    listenForRemoveFromFavourites();
    listenForUserLogin();
    listenForUserSignUp();
    listenForUserLogout();
    globalThis.ikea.mvAgent = globalThis.ikea.mvAgent || {};
    const favouritesApi = {
      removeItemFromState,
      agentLoaded: true,
    };
    (globalThis.ikea as unknown as { favourites: typeof favouritesApi }).favourites = {
      removeItemFromState: (itemNo: string) => {
        console.warn('Deprecated: use ikea.mvAgent.favourites.removeItemFromState instead');
        return favouritesApi.removeItemFromState(itemNo);
      },
      agentLoaded: true,
    };
    globalThis.ikea.mvAgent.favourites = favouritesApi;

    if (!favouritesAgentLoaded) {
      setFavouritesAgentLoaded(true);
      document.body.dispatchEvent(new Event(Actions.FAVOURITES_AGENT_LOADED, { bubbles: true, cancelable: true }));
      globalThis.ikea.pubsub.publish(Actions.FAVOURITES_AGENT_LOADED, null);
      if (signUpRedirect) {
        userLoginOrSignUpCallback(true);
      } else if (loginRedirect) {
        userLoginOrSignUpCallback(false);
      }
    }
  }, [
    favouritesAgentLoaded,
    listenForAddToFavourites,
    listenForRemoveFromFavourites,
    listenForUserLogin,
    listenForUserLogout,
    listenForUserSignUp,
    loginRedirect,
    removeItemFromState,
    signUpRedirect,
    userLoginOrSignUpCallback,
  ]);

  const execUnsubscribeListeners = useCallback(() => {
    if (!globalThis?.ikea && !globalThis.ikea?.pubsub) {
      return;
    }
    globalThis.ikea.pubsub.unsubscribe(Actions.ADD_TO_FAVOURITES_ACTION, addToFavouritesListenerCallback);
    globalThis.ikea.pubsub.unsubscribe(Actions.REMOVE_FROM_FAVOURITES_ACTION, removeFromFavouritesListenerCallback);
    globalThis.ikea.pubsub.unsubscribe(Actions.USER_LOGGED_IN, userLoginCallback);
    globalThis.ikea.pubsub.unsubscribe(Actions.PROFILE_SIGN_UP, userSignUpCallback);
    globalThis.ikea.pubsub.unsubscribe(Actions.USER_LOGGED_OUT, userLogoutCallback);
  }, [
    addToFavouritesListenerCallback,
    removeFromFavouritesListenerCallback,
    userLoginCallback,
    userSignUpCallback,
    userLogoutCallback,
  ]);

  const mvFavouritesAgent = useCallback(async () => {
    if (document.readyState === 'complete' || globalThis.ikea.pubsub) {
      execSubscribeListeners();
    } else {
      globalThis.addEventListener('load', execSubscribeListeners);
    }
  }, [execSubscribeListeners]);

  const unsubscribePubsub = useCallback(() => {
    if (document.readyState === 'complete' || globalThis.ikea.pubsub) {
      execUnsubscribeListeners();
    } else {
      globalThis.addEventListener('load', execUnsubscribeListeners);
    }
  }, [execUnsubscribeListeners]);

  useEffect(() => {
    mvFavouritesAgent();
    return unsubscribePubsub;
  }, [mvFavouritesAgent, unsubscribePubsub]);

  return {
    listsToAddItem,
    itemToAdd,
    showAddItemToListModal,
    setShowAddItemToListModal,
    addToFavouritesSuccess,
    addToFavouritesFail,
    itemToRemove,
    listsToRemoveItem,
    removedLists,
    setShowRemovedListsModal,
    showAddOrRemoveModal,
    showRemovedListsModal,
    setShowAddOrRemoveModal,
    removeFromFavouritesSuccess,
    removeFromFavouritesFail,
    removeFromList,
    getLists,
  };
};

/**
 * Returns a cached promise for getLists
 */
function useGetLists() {
  const [lists, setLists] = useState<ReturnType<typeof getLists>>();
  const { getSession } = useContext(IdentityContext);

  return useCallback(
    (refetch = false) => {
      if (!lists || refetch) {
        const newLists = getSession().then(() => getLists());
        setLists(newLists);
        return newLists;
      }
      return lists;
    },
    [getSession, lists],
  );
}
